import { CascadiaMiniIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { PropsWithChildren } from "react";
import { Button } from "react-aria-components";

import { Icon } from "../foundations/icons/Icon/Icon";
import { getTextStyle } from "../foundations/typography/styles";

export interface Props extends PropsWithChildren {
  leftIcon?: CascadiaMiniIcon;
  rightIcon?: CascadiaMiniIcon;
  clearable?: boolean;
  compact?: boolean;
  onClear?: () => void;
  onToggle?: () => void;
  isDisabled?: boolean;
}

export function InputContainer(props: Props) {
  const {
    leftIcon,
    clearable,
    rightIcon,
    compact,
    onToggle,
    onClear,
    isDisabled,
  } = props;

  const iconPadding = compact
    ? `calc(${"var(--space-2-5)"} + 20px + ${"var(--space-1-5)"})`
    : `calc(${"var(--space-3)"} + 20px + ${"var(--space-2-5)"})`;

  return (
    <div
      className={css(styles.container, {
        ".content": {
          width: "100%",
          display: "flex",
          flex: 1,
          "button, input": css(
            styles.base,
            styles.borders,
            compact && styles.compact,
            leftIcon && {
              paddingLeft: iconPadding,
            },
            rightIcon && {
              paddingRight: iconPadding,
            },
            getTextStyle("input/text-field")
          ),
        },
      })}
    >
      {leftIcon && (
        <div className={css(styles.icon)}>
          <Icon
            type="mini"
            name={leftIcon}
            color={compact || leftIcon === "search" ? "icon-weak" : "icon-base"}
            size="space-5"
          />
        </div>
      )}

      <div className="content">{props.children}</div>

      {clearable && (
        <Button
          onPress={onClear}
          className={css(
            styles.icon,
            {
              "&[data-focused]": {
                boxShadow: "var(--focus-input)",
              },
              borderBottomRightRadius: "var(--border-radius-2)",
              borderTopRightRadius: "var(--border-radius-2)",
              cursor: "pointer",
              paddingLeft: "var(--space-2-5)",
              right: 0,
            },
            compact && {
              borderBottomRightRadius: "var(--border-radius-half)",
              borderTopRightRadius: "var(--border-radius-half)",
            }
          )}
        >
          <Icon type="mini" name="cancel" color="icon-weak" size="space-5" />
        </Button>
      )}

      {!clearable && rightIcon && (
        <button
          tabIndex={-1}
          onClick={onToggle}
          type="button"
          disabled={isDisabled}
          className={css(
            {
              all: "unset",
              right: 0,
              cursor: "pointer",
            },
            styles.icon
          )}
        >
          <Icon type="mini" name={rightIcon} color="icon-weak" size="space-5" />
        </button>
      )}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  }),
  borders: css({
    background: "var(--background-input)",
    boxShadow: `inset 0px 0px 0px 1px var(--border-input)`,
    color: "var(--text-base)",

    "&[data-hovered]": {
      boxShadow: `inset 0px 0px 0px 1px var(--border-input-hover)`,
    },
    "&[data-focused]": {
      boxShadow: "var(--focus-input)",
    },

    "&[data-invalid]": {
      boxShadow: `inset 0px 0px 0px 1px var(--border-danger-input)`,
      "&[data-hovered]": {
        boxShadow: `inset 0px 0px 0px 1px var(--border-danger-input)`,
      },
      "&[data-focused]": {
        boxShadow: "var(--focus-input-error)",
      },
    },

    "&[data-disabled]": {
      boxShadow: `inset 0px 0px 0px 1px var(--border-input-disabled)`,
      backgroundColor: "var(--background-input-disabled)",
      "&[data-hovered]": {
        boxShadow: `inset 0px 0px 0px 1px var(--border-input-disabled)`,
      },
      "&[data-focused]": {
        boxShadow: `inset 0px 0px 0px 1px var(--border-input-disabled)`,
      },
    },
  }),
  base: css({
    alignItems: "center",
    borderWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    flexGrow: 1,
    gap: "var(--space-1-5)",
    outline: "none",
    width: "100%",
    borderRadius: `var(--border-radius-2)`,
    minHeight: 40,
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    paddingTop: "var(--space-2)",
    "&::-webkit-search-cancel-button, &::-webkit-search-decoration": {
      WebkitAppearance: "none",
    },
  }),
  compact: css({
    borderRadius: "var(--border-radius-half)",
    minHeight: 32,
    paddingBottom: "var(--space-1)",
    paddingLeft: "var(--space-2-5)",
    paddingRight: "var(--space-2)",
    paddingTop: "var(--space-1)",
  }),
  icon: css({
    alignItems: "center",
    background: "transparent",
    border: "none",
    bottom: 0,
    display: "flex",
    outline: "none",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    position: "absolute",
    top: 0,
  }),
};
